import { EmptyState } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';

/**
 * A screen the sidebar already links to, whose module has not been built yet.
 *
 * The public site had a placeholder page once and it was deleted the moment the
 * last route it stood in for was built (see the note in `apps/web/src/App.tsx`).
 * This one is a different proposition: docs/05-ADMIN-DASHBOARD.md §1 asks for
 * permission-aware navigation over §2's whole route table, which cannot be
 * demonstrated — or tested against the §3 matrix — with one screen behind it.
 * Every path here is scheduled inside this same phase, and each placeholder
 * names the step that replaces it, so an unfinished module reads as unfinished
 * rather than as broken.
 */
export interface ModulePlaceholderProps {
  /** The `nav.items.*` key, so the title is the sidebar's own label. */
  navKey: string;
  /** The numbered step in docs/09-BUILD-PLAN.md Phase 6 that delivers it. */
  step: number;
}

export function ModulePlaceholder({ navKey, step }: ModulePlaceholderProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-h2 font-extrabold text-primary">{t(`nav.items.${navKey}`)}</h1>

      <EmptyState
        className="mt-8 py-12 text-center"
        title={t('placeholder.title')}
        /*
         * A module whose wait is not simply "a later step of this phase" says
         * so in its own words. The migration console is the case: it is a UI
         * over the `wp:*` commands, and those are Phase 7 — quoting a step
         * number from Phase 6 would promise a date the plan does not.
         */
        description={t(`placeholder.notes.${navKey}`, {
          defaultValue: t('placeholder.body', { step }),
        })}
      />
    </div>
  );
}
